import { LinkIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/button';
import { Dragabble } from '@/components/drag-drop';
import { Widget } from '@/components/widget';
import { useTree } from '@/hooks/use-tree';
import { TreeAction, TreeState } from '@/tree-store';
import { useUserContainers } from '@/hooks/user/use-user-containers';
import { useTreeStore } from '@/context/tree-context';
import { SyntheticEvent, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ComboBox } from '@/components/ui/combo-box';
import { ExplorerFlow } from '@/features/explorer/components/explorer-flow';
import { ExplorerTable } from '@/features/explorer/components/explorer-table';

interface ExplorerWidgetProps {
  id?: string;
  defaultViewMode?: 'flow' | 'table';
  defaultEsQueries?: {
    container: string;
    query: string;
  }[];
  hideable?: boolean;
}

const treeSelector = (state: TreeState & TreeAction) => ({
  container: state.container,
  nodes: state.nodes,
  edges: state.edges,
  onNodesChange: state.onNodesChange,
  onEdgesChange: state.onEdgesChange,
  setContainer: state.setContainer,
  setSearchStringNode: state.setSearchStringNode,
});

export function ExploreWidget({
  id = 'explorer',
  defaultViewMode = 'flow',
  defaultEsQueries,
  hideable = false,
}: ExplorerWidgetProps) {
  const [viewMode, setViewMode] = useState<'flow' | 'table'>(defaultViewMode);

  const { data } = useUserContainers();
  const queryClient = useQueryClient();

  const { nodes, setContainer, setSearchStringNode } =
    useTreeStore(treeSelector);

  useEffect(() => {
    if (!data?.containers.length) {
      return;
    }

    setSelectedContainer(data.containers[0]);

    setParams({
      container: data.containers[0].value,
      searchString: null,
    });

    setContainer(data.containers[0].value);
  }, [data]);

  const [params, setParams] = useState<{
    container: string | null;
    searchString: string | null;
  }>({ container: null, searchString: null });

  const { refetch } = useTree(params, {
    enabled: !!params.container || !!params.searchString,
  });

  const [selectedContainer, setSelectedContainer] = useState<
    { label: string; value: string } | null | undefined
  >(data?.containers[0]);

  function handleSubmit(event: SyntheticEvent) {
    event.preventDefault();

    const target = event.target as HTMLFormElement;
    const formData = new FormData(target);
    const container = formData.get('container[value]') as string | null;
    const searchString = formData.get('searchString') as string | null;
    let allowRefetch = false;

    if (nodes[1]?.id) {
      setSearchStringNode(nodes[1].id, searchString);
    }

    if (params.container !== container) {
      queryClient.removeQueries(['container_objects', 'container']);
    }

    if (
      params.container === container &&
      params.searchString === searchString
    ) {
      allowRefetch = true;
    }

    if (container || searchString) {
      setParams({
        container,
        searchString,
      });
    }

    if (container) {
      setContainer(container);
    }

    if (allowRefetch) {
      refetch();
    }
  }

  function handleToggleViewMode() {
    setViewMode(viewMode === 'flow' ? 'table' : 'flow');
  }

  return (
    <Widget
      id={id}
      title="Explorer"
      toolbar={
        <>
          <Button type="button" onClick={handleToggleViewMode}>
            {viewMode === 'flow' ? (
              <TableCellsIcon className="h-4 w-4" aria-hidden="true" />
            ) : (
              <svg
                className="w-4 h-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="1.5"
                  d="M7.926 10.898 15 7.727m-7.074 5.39L15 16.29M8 12a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Zm12 5.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Zm0-11a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Z"
                />
              </svg>
            )}
          </Button>
          <Dragabble value={{ widgetId: id }} type="link">
            <Button>
              <LinkIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Dragabble>
        </>
      }
      hideable={hideable}
      minHeight={500}
    >
      <div className="absolute z-10 w-[500px]">
        <form onSubmit={handleSubmit}>
          <div className="flex items-end space-x-2">
            <div className="flex-1">
              {data && data.containers && (
                <ComboBox
                  label="Container"
                  options={data.containers}
                  name="container"
                  value={selectedContainer ?? { label: '', value: '' }}
                  onChange={(value) => setSelectedContainer(value)}
                />
              )}
            </div>
            <input
              type="search"
              name="searchString"
              id="searchString"
              className="block flex-1 w-full rounded-md border py-2 text-gray-900 shadow-sm border-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-indigo-500 sm:text-sm sm:leading-5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Your keywords"
            />
            <Button type="submit">Search</Button>
          </div>
        </form>
      </div>
      {viewMode === 'flow' ? (
        <ExplorerFlow id={id} />
      ) : (
        <ExplorerTable
          id={id}
          container={params.container || 'components_index'}
          search={params.searchString || ''}
          defaultEsQueries={defaultEsQueries}
        />
      )}
    </Widget>
  );
}
